import { useEffect } from 'react'
import { Link } from '@inertiajs/react'
import Seo from '../components/Seo'
import PackArtwork from '../components/PackArtwork'
import type { AdsConversion, Pack } from '../lib/types'

interface OrderInfo {
  id: number
  email: string
  total: string
  created_at: string
}

export default function ThankYou({
  order,
  packs,
  conversion,
}: {
  order: OrderInfo
  packs: Pack[]
  conversion?: AdsConversion | null
}) {
  // Report the purchase to Google Ads once, client-side. gtag.js is loaded
  // globally by the layout only when a conversion ID is set; guard for SSR and
  // for the tag being absent. Google dedupes repeats via transaction_id.
  useEffect(() => {
    if (!conversion || typeof window === 'undefined' || typeof window.gtag !== 'function') return
    window.gtag('event', 'conversion', {
      send_to: conversion.send_to,
      value: conversion.value,
      currency: conversion.currency,
      transaction_id: conversion.transaction_id,
    })
  }, [conversion])

  return (
    <>
      <Seo title="Order confirmed" noindex />
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-16 text-center">
        <div className="mono-label">Order #{order.id}</div>
        <h1 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tight">Thank you.</h1>
        <p className="mt-4 text-muted">
          Your order is confirmed. A receipt was sent to {order.email}.
        </p>

        <div className="mt-10 border border-line divide-y divide-line text-left">
          {packs.map((pack) => (
            <div key={pack.id} className="flex items-center gap-4 p-4">
              <div className="size-14 shrink-0 border border-line overflow-hidden">
                <PackArtwork pack={pack} size="thumb" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{pack.name}</div>
                <div className="mono-label mt-0.5">{pack.type_label}</div>
              </div>
              <span className="mono-label !text-emerald-400">Ready</span>
            </div>
          ))}
        </div>

        <Link
          href="/account"
          className="inline-grid place-items-center mt-10 h-12 px-8 bg-paper text-ink text-sm font-semibold hover:opacity-90 transition"
        >
          Go to downloads →
        </Link>
      </div>
    </>
  )
}
